"use client";

import { FormEvent, useMemo, useState } from "react";
import Link from "next/link";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getProcedureQuickSubmitConfig,
  isFieldRequired,
  isFieldVisible,
  type PatternTemplate,
  type QuickSubmitField,
} from "@/lib/quick-submit/procedure-quick-submit";
import { cn } from "@/lib/utils";

type ProcedureQuickSubmitWidgetProps = {
  procedureId: string;
  procedureSlug: string;
  patternTemplate: PatternTemplate;
  verticalSlug: string;
};

type FormValues = Record<string, string | string[]>;

const emptyValues: FormValues = {};

export function ProcedureQuickSubmitWidget({
  procedureId,
  procedureSlug,
  patternTemplate,
  verticalSlug,
}: ProcedureQuickSubmitWidgetProps) {
  const config = getProcedureQuickSubmitConfig(procedureSlug);
  const initialValues = useMemo(() => {
    if (!config) return emptyValues;
    return Object.fromEntries(
      config.fields.map((field) => [
        field.id,
        field.type === "multi-select"
          ? []
          : field.fixedValue ?? (field.id === "price_basis" ? config.defaultPriceBasis : ""),
      ]),
    ) as FormValues;
  }, [config]);
  const [values, setValues] = useState<FormValues>(initialValues);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!config) return null;

  const errors = getClientErrors(config.fields, values);
  const visibleFields = config.fields.filter((field) => isFieldVisible(field, values));
  const hasErrors = Object.keys(errors).length > 0;

  function setValue(field: QuickSubmitField, value: string | string[]) {
    setValues((current) => ({
      ...current,
      [field.id]: value,
    }));
    setSubmitError(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTouched(Object.fromEntries(visibleFields.map((field) => [field.id, true])));

    if (hasErrors) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch("/api/submit/procedure-quick", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          procedureId,
          procedureSlug,
          patternTemplate,
          verticalSlug,
          fields: values,
        }),
      });
      const body = await response.json().catch(() => ({}));

      if (!response.ok || !body.success) {
        setSubmitError(body.error ?? "We could not save this price. Please try again.");
        return;
      }

      setSuccess(true);
    } catch {
      setSubmitError("We could not save this price. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <section className="mt-10 max-w-4xl rounded-2xl border border-emerald-200 bg-white p-6 shadow-sm md:p-8">
        <div className="flex items-start gap-4">
          <CheckCircle2 className="mt-1 h-6 w-6 shrink-0 text-emerald-700" aria-hidden="true" />
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-950">Thanks for sharing your price.</h2>
            <p className="mt-2 text-sm font-medium leading-6 text-slate-600">
              Your anonymous price report has been saved for private aggregation.
            </p>
            <Link
              href={`/submit?procedure=${encodeURIComponent(procedureSlug)}`}
              className="mt-5 inline-flex min-h-[44px] items-center gap-2 text-sm font-bold text-emerald-700 hover:text-emerald-800"
            >
              Give more detail
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-10 max-w-4xl rounded-2xl border border-black/5 bg-white p-5 shadow-sm md:p-8">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Anonymous price report</p>
        <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">Share what you paid for {config.procedureName.toLowerCase()}</h2>
        <p className="mt-2 text-sm font-medium leading-6 text-slate-600">
          State only. No contact details, account or clinical story.
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          {config.fields.map((field) => {
            if (!isFieldVisible(field, values)) return null;
            const value = values[field.id];
            const error = touched[field.id] ? errors[field.id] : undefined;

            return (
              <FieldControl
                key={field.id}
                field={field}
                value={value}
                error={error}
                values={values}
                onBlur={() => setTouched((current) => ({ ...current, [field.id]: true }))}
                onChange={(nextValue) => setValue(field, nextValue)}
              />
            );
          })}
        </div>

        {submitError && (
          <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm font-semibold text-rose-800" role="alert">
            {submitError}
          </p>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Button
            type="submit"
            disabled={submitting || hasErrors}
            className="min-h-[48px] rounded-xl px-6"
          >
            {submitting ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : null}
            Submit price
          </Button>
          <Link
            href={`/submit?procedure=${encodeURIComponent(procedureSlug)}`}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900 sm:justify-start"
          >
            Give more detail
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </form>
    </section>
  );
}

function FieldControl({
  field,
  value,
  error,
  values,
  onBlur,
  onChange,
}: {
  field: QuickSubmitField;
  value: string | string[] | undefined;
  error?: string;
  values: FormValues;
  onBlur: () => void;
  onChange: (value: string | string[]) => void;
}) {
  const required = isFieldRequired(field, values);
  const errorId = `${field.id}-error`;
  const label = `${field.label}${required ? "" : " (optional)"}`;

  if (field.type === "multi-select") {
    const selectedValues = Array.isArray(value) ? value : [];

    return (
      <fieldset className="sm:col-span-2" onBlur={onBlur}>
        <legend className="mb-2 text-sm font-bold text-slate-800">{label}</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {field.options?.map((option) => {
            const checked = selectedValues.includes(option);
            return (
              <label
                key={option}
                className={cn(
                  "flex min-h-[44px] cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 text-sm font-semibold",
                  checked ? "border-emerald-600 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-white text-slate-700",
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(event) => {
                    onChange(
                      event.target.checked
                        ? [...selectedValues, option]
                        : selectedValues.filter((item) => item !== option),
                    );
                  }}
                  className="h-5 w-5 rounded border-slate-300 text-emerald-700 focus:ring-emerald-600"
                />
                {option}
              </label>
            );
          })}
        </div>
        <FieldError id={errorId} error={error} />
      </fieldset>
    );
  }

  return (
    <label className="block">
      <span className="mb-2 block text-sm font-bold text-slate-800">{label}</span>
      {field.type === "select" ? (
        <select
          value={typeof value === "string" ? value : field.fixedValue ?? ""}
          disabled={Boolean(field.fixedValue)}
          required={required}
          onBlur={onBlur}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 disabled:bg-slate-50"
        >
          {!field.fixedValue && <option value="">Choose an option</option>}
          {field.options?.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="number"
          inputMode="numeric"
          min={field.min}
          max={field.max}
          step={field.step}
          value={typeof value === "string" ? value : ""}
          required={required}
          onBlur={onBlur}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20"
          placeholder={field.type === "currency" ? "AUD" : undefined}
        />
      )}
      <FieldError id={errorId} error={error} />
    </label>
  );
}

function FieldError({ id, error }: { id: string; error?: string }) {
  if (!error) return null;
  return (
    <p id={id} className="mt-2 text-sm font-semibold text-rose-700" role="alert">
      {error}
    </p>
  );
}

function getClientErrors(fields: QuickSubmitField[], values: FormValues) {
  const errors: Record<string, string> = {};

  for (const field of fields) {
    if (!isFieldVisible(field, values)) continue;
    const required = isFieldRequired(field, values);
    const value = field.fixedValue ?? values[field.id];

    if (field.type === "multi-select") {
      const selected = Array.isArray(value) ? value : [];
      if (required && selected.length === 0) errors[field.id] = "Please choose at least one item.";
      if (field.max && selected.length > field.max) errors[field.id] = "Please choose fewer items.";
      continue;
    }

    if (value === undefined || value === "") {
      if (required) errors[field.id] = field.type === "currency" ? "Please enter the price you paid, in AUD." : "Please choose an option.";
      continue;
    }

    if (field.type === "currency" || field.type === "number") {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) errors[field.id] = "Please enter numbers only.";
      if (field.min !== undefined && numericValue < field.min) errors[field.id] = "Please enter a value in the allowed range.";
      if (field.max !== undefined && numericValue > field.max) errors[field.id] = "Please enter a value in the allowed range.";
    }
  }

  return errors;
}
